<template>
  <div :id="page_slug">

    <div class="crumbs-bar">
      <el-row>
        <el-col :span="12">
          <el-breadcrumb separator-class="el-icon-arrow-right">
            <el-breadcrumb-item :to="{ name: 'DashboardPage' }">Dashboard</el-breadcrumb-item>
            <el-breadcrumb-item>Templates</el-breadcrumb-item>
          </el-breadcrumb>        
        </el-col>      
      </el-row>
    </div>

    <div class="actions-bar">
      <el-row> 
        <el-col :span="8">
          <el-input placeholder="" v-model="search.search_terms" v-on:keyup.native="whichSearchFunctionToUse" class="input-with-select" style="width:400px">
            <el-select @change="whichSearchFunctionToUse" :clearable="true" class="search_dropdown" v-model="search.corporation" slot="prepend" placeholder="Select Corporation">
              <el-option-group>
                <el-option
                  label="All Corporations"
                  value="search_all_corporations">
                </el-option>
              </el-option-group>

              <el-option-group
                label="Corporations">
                <el-option
                  v-for="item in corporations"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id">
                </el-option>
              </el-option-group>
            </el-select>
            <el-button @click="whichSearchFunctionToUse" slot="append" icon="el-icon-search"></el-button>
          </el-input>
        </el-col>

        <el-col :span="14" :offset="2" class="text-right">
          <el-dropdown trigger="click" :hide-on-click="false">
            <el-tooltip class="item" effect="light" content="Show or hide columns" placement="top">
              <el-button>
                Columns <i class="el-icon-caret-bottom"></i>
              </el-button>
            </el-tooltip>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-for="(column, index) in columns" :key="index">
                <el-checkbox v-model="columns[index]">{{index | prettyIndexName}}</el-checkbox>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-button :disabled="!hasRole('templates.can_import')" type="default" @click="importTemplates()">Import Templates</el-button>

          <download-excel
            class="el-button el-button--default"
            :data="templates"
            :fields="template_headings"
            name="Templates.xls">
            Download Templates
          </download-excel>
        </el-col>
      </el-row>
    </div>

    <el-row>
      <el-table
        v-loading="loading.templates"
        :data="templates"
        border
        resizable="true"
        style="width: 100%">

        <el-table-column
          align="center"
          width="115%">
          <template slot-scope="scope">
            <img v-if="scope.row.optimised_thumbnail_url != ''" :src="scope.row.optimised_thumbnail_url" class="template-thumb"/>

            <el-button 
              v-else
              type="text" 
              size="small"
              @click="optimiseImage(scope.row.id)">
              Optimise
            </el-button>

            <!-- <img v-else :src="scope.row.thumbnail_url" class="template-thumb"/> -->
          </template>
        </el-table-column>

        <el-table-column
          v-if="columns.id"
          width="115%"
          prop="id"
          label="Template ID">
        </el-table-column>

        <el-table-column
          v-if="columns.name"
          prop="name"
          label="Name">
        </el-table-column>

        <el-table-column
          v-if="columns.display_name"
          prop="display_name"
          label="Display Name">
        </el-table-column>

        <el-table-column
          v-if="columns.description"
          prop="description"
          label="Description">
        </el-table-column>

        <el-table-column
          v-if="columns.size"
          prop="size"
          label="Size">
        </el-table-column>

        <el-table-column
          v-if="columns.thumbnail_url"
          prop="thumbnail_url"
          label="Thumbnail URL">
        </el-table-column>

        <el-table-column
          v-if="columns.product_id"
          prop="product_id"
          label="Product ID">
        </el-table-column>

        <el-table-column
          v-if="columns.company_id"
          prop="company_id"
          label="Company ID">
        </el-table-column>

        <el-table-column
          v-if="columns.editable_products"
          prop="editable_products"
          label="Editable Products">
        </el-table-column>

        <el-table-column
          v-if="columns.corporation"
          prop="corporation.name"
          label="Corporation">
        </el-table-column>

        <el-table-column
          v-if="columns.created_at"
          align="center"
          width="115%"
          prop="created_at"
          label="Created Date">
          <template slot-scope="scope">
            {{scope.row.created_at | moment(config.date_format_compact)}}
          </template>          
        </el-table-column>

        <el-table-column
          v-if="columns.updated_at"
          align="center"
          width="115%"
          prop="updated_at"
          label="Updated Date">
          <template slot-scope="scope">
            {{scope.row.updated_at | moment(config.date_format_compact)}}
          </template>          
        </el-table-column>

        <el-table-column
          v-if="columns.is_private"
          align="center"
          width="115%"
          prop="is_private"
          label="Private">
          <template slot-scope="scope">
            <el-switch
              disabled
              :active-value="1"
              :inactive-value="0"
              v-model="scope.row.enabled">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column
          label="Actions"
          align="center"
          width="115%">
          <template slot-scope="scope">
            <router-link :to="{ name: 'TemplateEdit', params: { id: scope.row.id }}">
              <el-button 
                :disabled="!hasRole('templates.can_edit')"
                icon="el-icon-edit"
                type="default" 
                size="small">
              </el-button>
            </router-link>

            <el-button 
              :disabled="!hasRole('templates.can_delete')"
              icon="el-icon-delete" 
              type="default" 
              size="small"
              @click="deleteTemplate(scope.row.id)">
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-row>

    <el-row :gutter="0">
      <el-col :span="24" :xs="24" :sm="24" class="pagination">
        <el-pagination
          background
          @current-change="handleCurrentChange"
          :current-page.sync="pagination.current_page"
          :page-count="pagination.total_pages"
          :page-size="pagination.per_page"
          layout="total, prev, pager, next"
          :total="pagination.total">
        </el-pagination>
      </el-col>
    </el-row>

    <pre>{{$data}}</pre>

  </div>
</template>

<style scoped>

.template-thumb{
  width:50px;
  height:auto;
}

.search_dropdown {
  width: 175px;
}

</style>

<script>
  import Config from '@/store/Config.js';
  import Tables from '@/store/Tables.js';
  import store from '@/store/Store.js';

  export default {
    data: function() {
      return {
        page_alias: 'Template List',
        page_slug: 'template-list',

        permissions: store.permissions,
        config: Config.config,
        columns: Tables.templates.columns,
        order_by: Tables.templates.order_by,
        sort_by: Tables.templates.sort_by,
            
        loading: {
          templates: true,
          image_optimisation: false
        },

        templates: [],
        corporations: [],

        search: {
          search_terms: '',
          corporation: '',
        },

        template_headings: {
          'ID': 'id',
          'Name': 'name',
          'Display Name': 'display_name',
          'Description': 'description',
          'Size': 'size',
          'Thumbnail URL': 'thumbnail_url',
          'Product ID': 'product_id',
          'Company ID': 'company_id',
          'Editable Products': 'editable_products',
          'Private': 'is_private',
          'Created At': 'created_at',
          'Updated At': 'updated_at',
        },

        pagination: {
          total: 0,
          per_page: 0,
          current_page: 0,
          last_page: 0,
          next_page_url: '',
          prev_page_url: '',
          from: 0,
          to: 0,
        }
      };
    },

    mounted: function() {
      const me = this;
      me.getTemplates();
      me.getCorporations();
    },

    methods: {
      hasRole: function(role_name) {
        const me = this;

        if(me.permissions.user_roles.length > 0) {
          /* returns undefined if doesn't find the role */
          var role = _.find(me.permissions.user_roles, function(o) { 
            return o.name == role_name; 
          });

          /* doesn't enter if when undefined */
          if(role) {
            return true
          }
        }
        
        return false;
      },

      importTemplates: function() {
        console.log('Import Templates Function');
        const me = this;

        me.loading.templates = true;

        const currentTmeplateCount = me.pagination.total;
        console.log(currentTmeplateCount);

        me.$http.get(me.config.api + 'printui').then(
          response => {
          console.log('Import Templates Function - Success Getting Data')  

          me.getTemplates();
          
          const newCount = me.pagination.total;

          me.$notify({
            title: 'Success',
            message: 'The Import Is Complete',
            type: 'success'
          });

          me.loading.templates = false;
        },response => {
          console.log('Import Templates Function - Error Getting Data');

          me.$notify({
            title: 'Error',
            message: response.statusText,
            type: 'error'
          });
          me.loading.templates = false;
        });
      },

      getTemplates: function(goToPage) {
        console.log('Get Templates Function');
        const me = this;

        me.loading.templates = true;

        me.$http.get(me.config.api + 'template?backend=1', {params: {backend_call: 1, page: goToPage}}).then(
          response => {
          console.log('Get Templates Function - Success Getting Data')  

          me.templates = response.body.data;
          
          me.pagination.total = response.body.total;
          me.old_count = response.body.total;
          
          me.pagination.per_page = response.body.per_page;
          me.pagination.current_page = response.body.current_page;
          me.pagination.last_page = response.body.last_page;
          me.pagination.next_page_url = response.body.next_page_url;
          me.pagination.prev_page_url = response.body.prev_page_url;
          me.pagination.from = response.body.from;
          me.pagination.to = response.body.to;

          me.loading.templates = false;
        }, response => {
          console.log('Get Templates Function - Error Getting Data');

          me.$notify({
            title: 'Error',
            message: response.statusText,
            type: 'error'
          });
          
          me.loading.templates = false;
        });
      },

      getCorporations: function() {
        console.log('Get Corporations Function');
        const me = this;

        me.$http.get(me.config.api + 'corporation', {params: {backend_call: 1}}).then(
          response => {
          console.log('Get Corporations Function - Success Getting Data')  

          me.corporations = response.body.data;
        }, response => {
          console.log('Get Corporations Function - Error Getting Data');

          me.$notify({
            title: 'Error',
            message: response.statusText,
            stype: 'error'
          });
        });
      },

      whichSearchFunctionToUse: function() {
        console.log('Which Search Function To Use');
        const me = this;

        if(me.search.corporation == "" || me.search.corporation == "search_all_corporations") {
          me.searchTemplates()
        } else {
          me.searchTemplatesByCorporation();
        }
      },
      
      searchTemplates: function(goToPage) {
        console.log('Search Templates Function');
        const me = this;
        me.loading.templates = true;

        me.$http.get(me.config.api + 'template-search', {params: {search_terms: me.search.search_terms, admin: 1, page: goToPage}}).then(
          response => {
          console.log('Search Templates Function - Success Getting Data')  
          
          me.templates = response.body.data;
          me.handlePagination(response);

          me.loading.templates = false;
        }, response => {
          console.log('Search Templates Function - Error Getting Data');

          me.$notify({
            title: 'Error',
            message: response.statusText,
            type: 'error'
          });

          me.loading.templates = false;
        });
      },

      searchTemplatesByCorporation: function(goToPage) {
        console.log('Search Templates By Corporation Function');
        const me = this;
        me.loading.templates = true;

        me.$http.get(me.config.api + 'templates-search-corporation', {params: {page: goToPage, search_terms: me.search.search_terms, company_id: me.search.corporation}}).then(
          response => {
          console.log('Search Templates By Corporation Function - Success Getting Data')  
          
          me.templates = response.body.data;
          me.handlePagination(response);

          me.loading.templates = false;
        }, response => {
          console.log('Search Templates By Corporation Function - Error Getting Data');

          me.$notify({
              title: 'Error',
              message: response.statusText,
              type: 'error'
          });

          me.loading.templates = false;
        });
      },
      
      deleteTemplate: function(id) {
        console.log('Delete Template Function');
        const me = this;
        
        me.$confirm('This will permanently delete the Template - Continue?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          me.$http.delete(me.config.api + 'template/{id}', {params: {id : id}}).then(
            response => {
            console.log('Delete Template Function - Success Deleting Data')  

            me.getTemplates();

            me.$notify({
              title: 'Success',
              message: 'The Template Was Deleted',
              type: 'success',
            });
          }, response => {
            console.log('Delete Template Function - Error Deleting Data');
          });  
        }).catch(() => {
          this.$message({
            type: 'info',
            message: 'Delete Canceled'
          });          
        });
      },

      optimiseImage: function(id) {
        console.log('Optimise Image Function');
        const me = this;

        me.loading.templates = true;

        me.$http.get(me.config.api + 'generate-thumbnail/{id}', {params: {id: id}}).then(
          response => {
          console.log('Optimise Image Function - Success Getting Data')  

          me.getTemplates(me.pagination.current_page);
        }, response => {
          console.log('Optimise Image Function - Error Getting Data');
        });
      },

      handlePagination: function(response) {
        const me = this;

        me.pagination.total = response.body.total;
        me.pagination.per_page = response.body.per_page;
        me.pagination.current_page = response.body.current_page;
        me.pagination.last_page = response.body.last_page;
        me.pagination.next_page_url = response.body.next_page_url;
        me.pagination.prev_page_url = response.body.prev_page_url;
        me.pagination.from = response.body.from;
        me.pagination.to = response.body.to;
			},

      handleCurrentChange(val) {
        const me = this;

        if(me.search.corporation == "" && me.search.search_terms == "") {
          me.getTemplates(val)
          console.log('1');
        } else if (me.search.corporation == ""){
          me.searchTemplates(val);
          console.log('2');
        } else {
          me.searchTemplatesByCorporation(val);
          console.log('3');
        }

        window.scrollTo(0,0);
      } //LAST METHOD
    }, //END METHODS

    filters: {
      prettyIndexName(text) {
        var prettyName = text.replace(/_/g, ' ').toUpperCase();
        return prettyName
      }
    }  
  }
</script>


// WEBPACK FOOTER //
// src/pages/templates/TemplateList.vue